import type { CompleteSuccessStory } from '../../types';
import { TrendingUp, Quote } from 'lucide-react';

interface ResultsSectionProps {
  story: CompleteSuccessStory;
}

export function ResultsSection({ story }: ResultsSectionProps) {
  const resultsQuotes = story.customerQuotes.filter((q) => q.section === 'results');

  return (
    <section className="py-20 lg:py-32 bg-gradient-to-br from-primary to-secondary text-primary-foreground relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-white rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <span className="text-accent font-mono text-sm uppercase tracking-wider font-semibold">
            The Results
          </span>
          <h2 className="font-bold text-4xl lg:text-5xl mt-4 mb-6">
            Transformational Business Impact
          </h2>
          <p className="text-xl text-white/80 max-w-3xl mx-auto">
            Measurable improvements across every key performance indicator within 12 months of full
            deployment.
          </p>
        </div>

        <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-3xl p-8 lg:p-12">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/20">
                  <th className="text-left py-4 px-4 font-bold text-lg">Metric</th>
                  <th className="text-center py-4 px-4 font-bold text-lg">Before</th>
                  <th className="text-center py-4 px-4 font-bold text-lg">After</th>
                  <th className="text-center py-4 px-4 font-bold text-lg">Improvement</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {story.roiMetrics.map((metric) => {
                  const improvement = calculateImprovement(
                    metric.beforeValue,
                    metric.afterValue
                  );
                  return (
                    <tr key={metric.id} className="hover:bg-white/5 transition-colors">
                      <td className="py-6 px-4 font-medium">{metric.metricName}</td>
                      <td className="text-center py-6 px-4 font-mono text-white/70">
                        {metric.beforeValue}
                      </td>
                      <td className="text-center py-6 px-4 font-mono text-white/70">
                        {metric.afterValue}
                      </td>
                      <td className="text-center py-6 px-4">
                        <span className="inline-flex items-center gap-2 bg-green-500/20 text-green-300 px-4 py-2 rounded-full font-bold">
                          <TrendingUp className="w-5 h-5" />
                          {improvement}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {resultsQuotes.length > 0 && (
          <div className="mt-12 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 lg:p-12">
            <div className="flex items-start gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center">
                  <Quote className="w-8 h-8 text-white" />
                </div>
              </div>
              <div className="flex-1">
                <p className="text-2xl lg:text-3xl leading-relaxed italic mb-6">
                  {resultsQuotes[0].quoteText}
                </p>
                <div>
                  <p className="font-bold text-xl">{resultsQuotes[0].authorName}</p>
                  <p className="text-white/70">{resultsQuotes[0].authorTitle}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function calculateImprovement(before: string, after: string): string {
  const beforeNum = parseFloat(before.replace(/[^0-9.]/g, ''));
  const afterNum = parseFloat(after.replace(/[^0-9.]/g, ''));

  if (isNaN(beforeNum) || isNaN(afterNum)) {
    return 'Improved';
  }

  if (beforeNum === 0) {
    return `+${afterNum}`;
  }

  const percentChange = ((afterNum - beforeNum) / beforeNum) * 100;
  if (percentChange > 0) {
    return `+${Math.round(percentChange)}%`;
  } else if (percentChange < 0) {
    return `${Math.round(percentChange)}%`;
  }
  return 'No change';
}
